import React, { useState } from 'react';
import './IntegrationGuide.css';

const IntegrationGuide: React.FC = () => {
  const [isDarkTheme, setIsDarkTheme] = useState(() => {
    const saved = localStorage.getItem('eefx-theme');
    return saved !== null ? saved === 'dark' : true; // Default to dark theme
  });

  const toggleTheme = () => {
    const newTheme = !isDarkTheme;
    setIsDarkTheme(newTheme);
    localStorage.setItem('eefx-theme', newTheme ? 'dark' : 'light');
  };
  return (
    <div className={`integration-page ${isDarkTheme ? 'dark-theme' : 'light-theme'}`}>
      <div className="page-header">
        <div className="header-content">
          <div className="logo-container" style={{ display: 'flex' }}>
            <img src="/logo.webp" alt="EEFX Chart Logo" className="logo-image" style={{ width: 72, height: 'fit-content', marginRight: 5 }}/>
            <h1>EEFX Chart</h1>
          </div>
          <div className="subtitle">Transform Any MetaTrader EA into a Modern Web Trading Interface</div>
          <button 
            className="theme-toggle-btn"
            onClick={toggleTheme}
            title={isDarkTheme ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
            style={{ position: 'absolute', top: '20px', right: '20px' }}
          >
            {isDarkTheme ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5"/>
                <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      <div className="page-content">
        <div className="content-card">
          <div className="card-header">
            <h2>📈 EA Integration Guide</h2>
            <p>Add web chart synchronization to any Expert Advisor in 3 simple steps</p>
          </div>

          <div className="card-body">
            <div className="integration-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>📁 Add Include Line</h3>
                  <p>At the very top of your existing EA file, add this single line:</p>
                  <div className="code-block">
                    <code><span className="keyword">#include</span> <span className="string">"EEFXChart.mqh"</span></code>
                  </div>
                </div>
              </div>

              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>⚡ Add Initialization</h3>
                  <p>In your <strong>OnInit()</strong> function, add one line:</p>
                  <div className="code-block">
                    <code>
                      <span className="keyword">int</span> OnInit() {'{'}
                      <br />
                      &nbsp;&nbsp;EEFX_Initialize(); &nbsp;&nbsp;<span className="comment">// ← Add this line</span>
                      <br />
                      &nbsp;&nbsp;<span className="comment">// Your existing OnInit code stays unchanged</span>
                      <br />
                      &nbsp;&nbsp;<span className="keyword">return</span> INIT_SUCCEEDED;
                      <br />
                      {'}'}
                    </code>
                  </div>
                </div>
              </div>

              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>🔄 Add Sync Handlers</h3>
                  <p>Add these function calls to enable real-time synchronization:</p>
                  
                  <div className="code-block">
                    <code>
                      <span className="comment">// In your OnTick() function - add as first line:</span>
                      <br />
                      <span className="keyword">void</span> OnTick() {'{'}
                      <br />
                      &nbsp;&nbsp;EEFX_OnTick(); &nbsp;&nbsp;<span className="comment">// ← Add this line FIRST</span>
                      <br />
                      &nbsp;&nbsp;<span className="comment">// Your existing OnTick code stays unchanged</span>
                      <br />
                      {'}'}
                      <br />
                      <br />
                      <span className="comment">// In your OnChartEvent() function - add as first line:</span>
                      <br />
                      <span className="keyword">void</span> OnChartEvent(<span className="keyword">const int</span> id, <span className="keyword">const long</span> &lparam, 
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="keyword">const double</span> &dparam, <span className="keyword">const string</span> &sparam) {'{'}
                      <br />
                      &nbsp;&nbsp;EEFX_OnChartEvent(id, lparam, dparam, sparam); &nbsp;&nbsp;<span className="comment">// ← Add this line FIRST</span>
                      <br />
                      &nbsp;&nbsp;<span className="comment">// Your existing OnChartEvent code stays unchanged</span>
                      <br />
                      {'}'}
                      <br />
                      <br />
                      <span className="comment">// In your OnTradeTransaction() function - add as first line:</span>
                      <br />
                      <span className="keyword">void</span> OnTradeTransaction(<span className="keyword">const</span> MqlTradeTransaction &trans, 
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="keyword">const</span> MqlTradeRequest &request, 
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="keyword">const</span> MqlTradeResult &result) {'{'}
                      <br />
                      &nbsp;&nbsp;EEFX_OnTradeTransaction(trans, request, result); &nbsp;&nbsp;<span className="comment">// ← Add this line FIRST</span>
                      <br />
                      &nbsp;&nbsp;<span className="comment">// Your existing OnTradeTransaction code stays unchanged</span>
                      <br />
                      {'}'}
                    </code>
                  </div>
                </div>
              </div>
            </div>

            <div className="highlight-box">
              <h4>✅ That's It!</h4>
              <p>Your EA now has full EEFX Chart integration! When you run your EA, it will automatically create a session URL popup. Open that URL in your browser to see your modern web trading interface with real-time synchronization.</p>
            </div>

            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">🌐</div>
                <h4>Automatic Web Chart</h4>
                <p>Session URL popup with instant web access</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">⚡</div>
                <h4>Real-time Sync</h4>
                <p>All drawing objects sync instantly to web</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">📊</div>
                <h4>Trade Markers</h4>
                <p>Buy/sell signals appear on web chart</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">📈</div>
                <h4>OHLC Data</h4>
                <p>Live candlestick data and hover info</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">🔧</div>
                <h4>Zero Changes</h4>
                <p>Your existing EA logic stays unchanged</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">🎨</div>
                <h4>Modern UI</h4>
                <p>Professional TradingView-style interface</p>
              </div>
            </div>

            <div className="complete-template">
              <div className="template-header">📝 Complete Integration Template</div>
              <div className="code-block">
                <code>
                  <span className="comment">//+------------------------------------------------------------------+</span>
                  <br />
                  <span className="comment">//|                                                   YourEA.mq5     |</span>
                  <br />
                  <span className="comment">//+------------------------------------------------------------------+</span>
                  <br />
                  <span className="keyword">#include</span> <span className="string">"EEFXChart.mqh"</span> &nbsp;&nbsp;<span className="comment">// ← ADD THIS LINE</span>
                  <br />
                  <br />
                  <span className="comment">// Your existing input parameters stay the same</span>
                  <br />
                  <span className="keyword">input double</span> LotSize = 0.1;
                  <br />
                  <span className="comment">// ... all your other inputs</span>
                  <br />
                  <br />
                  <span className="keyword">int</span> OnInit() {'{'}
                  <br />
                  &nbsp;&nbsp;EEFX_Initialize(); &nbsp;&nbsp;<span className="comment">// ← ADD THIS LINE</span>
                  <br />
                  &nbsp;&nbsp;
                  <br />
                  &nbsp;&nbsp;<span className="comment">// All your existing OnInit code stays exactly the same</span>
                  <br />
                  &nbsp;&nbsp;<span className="comment">// ...</span>
                  <br />
                  &nbsp;&nbsp;
                  <br />
                  &nbsp;&nbsp;<span className="keyword">return</span> INIT_SUCCEEDED;
                  <br />
                  {'}'}
                  <br />
                  <br />
                  <span className="keyword">void</span> OnTick() {'{'}
                  <br />
                  &nbsp;&nbsp;EEFX_OnTick(); &nbsp;&nbsp;<span className="comment">// ← ADD THIS LINE FIRST</span>
                  <br />
                  &nbsp;&nbsp;
                  <br />
                  &nbsp;&nbsp;<span className="comment">// All your existing OnTick code stays exactly the same</span>
                  <br />
                  &nbsp;&nbsp;<span className="comment">// Your trading logic</span>
                  <br />
                  &nbsp;&nbsp;<span className="comment">// Your drawing logic</span>
                  <br />
                  &nbsp;&nbsp;<span className="comment">// Everything unchanged</span>
                  <br />
                  {'}'}
                  <br />
                  <br />
                  <span className="comment">// IF YOU HAVE OnChartEvent - add as first line:</span>
                  <br />
                  <span className="keyword">void</span> OnChartEvent(<span className="keyword">const int</span> id, <span className="keyword">const long</span> &lparam, 
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="keyword">const double</span> &dparam, <span className="keyword">const string</span> &sparam) {'{'}
                  <br />
                  &nbsp;&nbsp;EEFX_OnChartEvent(id, lparam, dparam, sparam); &nbsp;&nbsp;<span className="comment">// ← ADD THIS LINE FIRST</span>
                  <br />
                  &nbsp;&nbsp;<span className="comment">// Your existing OnChartEvent code stays unchanged</span>
                  <br />
                  {'}'}
                  <br />
                  <br />
                  <span className="comment">// IF YOU HAVE OnTradeTransaction - add as first line:</span>
                  <br />
                  <span className="keyword">void</span> OnTradeTransaction(<span className="keyword">const</span> MqlTradeTransaction &trans, 
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="keyword">const</span> MqlTradeRequest &request, 
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span className="keyword">const</span> MqlTradeResult &result) {'{'}
                  <br />
                  &nbsp;&nbsp;EEFX_OnTradeTransaction(trans, request, result); &nbsp;&nbsp;<span className="comment">// ← ADD THIS LINE FIRST</span>
                  <br />
                  &nbsp;&nbsp;<span className="comment">// Your existing OnTradeTransaction code stays unchanged</span>
                  <br />
                  {'}'}
                  <br />
                  <br />
                  <span className="comment">// All your existing functions stay exactly the same</span>
                  <br />
                  <span className="comment">// Your helper functions</span>
                  <br />
                  <span className="comment">// Your trading logic functions</span>
                  <br />
                  <span className="comment">// Everything unchanged</span>
                </code>
              </div>
            </div>

            <div className="cta-section">
              <h3>🚀 Ready to Transform Your Trading?</h3>
              <p>Turn any MetaTrader EA into a professional web trading interface with just 4 lines of code!</p>
              <div className="cta-buttons">
                <button className="btn-primary" onClick={() => window.open('/', '_self')}>
                  ← Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationGuide;